import { ChoicesAnswer, MappedAnswer } from '../components/context/SurveyPageContext';
import QuestionDTO from '../interfaces/dto/questionDTO';
import SurveyDTO from '../interfaces/dto/surveyDTO';
import Question from '../interfaces/question';
import { QuestionType } from '../interfaces/questionType';
import Survey from '../interfaces/survey';
import { getCancelFlow } from '../services/questionnaire';
import { betweenRequiredText, maxRequiredText, minRequiredText } from '../utils/questionLimits';

const buildValidation = (question: QuestionDTO) => {
  switch (question.type) {
    case QuestionType.CHOOSE_MANY: {
      return (answer: MappedAnswer | null): Array<string> => {
        const choiceAnswer = answer as ChoicesAnswer;
        const errors: Array<string> = [];
        const choicesSize = choiceAnswer?.choices?.length || 0;
        const min = question.minRequired;
        const max = question.maxRequired;
        if (!min && !max) return [];
        if (min && max && (choicesSize < min || choicesSize > max)) {
          errors.push(betweenRequiredText({ min, max }));
          return errors;
        }
        if (min && min > 0 && choicesSize < min) {
          errors.push(minRequiredText());
          return errors;
        }
        if (max && max > 0 && choicesSize > max) {
          errors.push(maxRequiredText({ max }));
          return errors;
        }
        return errors;
      };
    }
    default:
      return (): Array<string> => [];
  }
};

const processNestedQuestions = ({
  nestedQuestions,
}: {
  nestedQuestions: QuestionDTO[];
}): Question[] => {
  if (!nestedQuestions) return [];
  return nestedQuestions.map((nestedQuestion: QuestionDTO) => ({
    ...nestedQuestion,
    questions: [],
    isSubQuestion: true,
    validate: buildValidation(nestedQuestion),
  }));
};

const processFirstOrderQuestions = ({ questions }: { questions: QuestionDTO[] }): Question[] => {
  if (!questions) return [];
  return questions.map((question: QuestionDTO) => ({
    ...question,
    questions: processNestedQuestions({ nestedQuestions: question.questions }),
    isSubQuestion: false,
    validate: buildValidation(question),
  }));
};

const dtoToSurvey = ({ surveys }: { surveys: SurveyDTO[] }): Survey => {
  const [firstSurvey] = surveys;
  return {
    ...firstSurvey,
    questions: processFirstOrderQuestions({ questions: firstSurvey.questions }),
  };
};

const query = async (): Promise<Survey> => {
  const surveys = await getCancelFlow();
  return dtoToSurvey({ surveys });
};

const cancelFlowSurveyQuery = {
  key: 'cancelFlowSurvey',
  query,
};

export { dtoToSurvey, cancelFlowSurveyQuery };
